import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, FileBadge, BarChart3, Globe2, Eye, ShieldAlert, MapPin, UserCircle2, AlertTriangle, BookOpen, Layers } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

const COLORS = ["#4F46E5", "#06B6D4", "#8B5CF6", "#F59E0B", "#10B981", "#EC4899", "#3B82F6"];
const LEVEL_COLORS = { High: "#10B981", Moderate: "#F59E0B", Caution: "#EF4444" };
const LEVEL_BADGE = {
  High: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Moderate: "bg-amber-100 text-amber-700 border-amber-200",
  Caution: "bg-rose-100 text-rose-700 border-rose-200",
  Unknown: "bg-slate-100 text-slate-500 border-slate-200",
};

function HBarCard({ testId, title, data, dataKey, labelKey, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={testId}>
      <h3 className="font-heading text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      {data.length === 0 ? (
        <p className="text-sm text-slate-400">No data yet.</p>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(160, data.length * 36)}>
          <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
            <YAxis type="category" dataKey={labelKey} width={140} tick={{ fontSize: 11, fill: "#475569" }} />
            <Tooltip />
            <Bar dataKey={dataKey} radius={[0, 6, 6, 0]}>
              {color ? <Cell fill={color} /> : data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = () => {
    api.get("/admin/analytics/summary").then((res) => setData(res.data)).catch(() => setData(null));
    api.get("/admin/analytics/confidence-trends").then((res) => setTrends(res.data)).catch(() => setTrends(null));
  };
  useEffect(() => { load(); }, []);

  const runReset = async () => {
    if (confirmText !== "RESET") return;
    setResetting(true);
    try {
      await api.post("/admin/analytics/reset", { confirm: "RESET" });
      toast.success("Analytics data has been flushed");
      setResetOpen(false);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset analytics data");
    } finally {
      setResetting(false);
    }
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin/analytics">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="admin-analytics-heading">Analytics</h1>
        <Button
          size="sm"
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-100"
          data-testid="analytics-reset-button"
          onClick={() => setResetOpen(true)}
        >
          <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Reset Analytics Data
        </Button>
      </div>

      {!data && <p className="text-sm text-slate-400" data-testid="analytics-loading">Loading analytics…</p>}

      {data && (
        <Tabs defaultValue="behavior" data-testid="analytics-tabs">
          <TabsList className="mb-6">
            <TabsTrigger value="behavior" data-testid="analytics-tab-behavior">Behavior</TabsTrigger>
            <TabsTrigger value="uses" data-testid="analytics-tab-uses">Uses</TabsTrigger>
          </TabsList>

          <TabsContent value="behavior" data-testid="analytics-behavior-panel">
            <p className="text-xs text-slate-400 mb-6">Anonymous browsing behavior — traffic, engagement and where visitors are coming from.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="analytics-total-events-card">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-2"><Eye className="h-4 w-4" /> Total Events</div>
                <p className="text-3xl font-bold font-mono text-slate-900">{data.behavior.total_events}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="analytics-unique-visitors-card">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-2"><Globe2 className="h-4 w-4" /> Unique Visitors (by IP)</div>
                <p className="text-3xl font-bold font-mono text-slate-900">{data.behavior.unique_visitors}</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="analytics-daily-trend-card">
              <h3 className="font-heading text-sm font-semibold text-slate-700 mb-4">Events Over Time</h3>
              {data.behavior.daily_trend.length === 0 ? (
                <p className="text-sm text-slate-400">No data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.behavior.daily_trend} margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#4F46E5" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <HBarCard testId="analytics-event-breakdown-card" title="Events by Type" data={data.behavior.event_breakdown} dataKey="count" labelKey="event_type" />
              <HBarCard testId="analytics-country-breakdown-card" title="Visitors by Country (Location)" data={data.behavior.country_breakdown} dataKey="count" labelKey="country" color="#4F46E5" />
            </div>
            <HBarCard testId="analytics-city-breakdown-card" title="Visitors by City (Location)" data={data.behavior.city_breakdown} dataKey="count" labelKey="city" color="#06B6D4" />
          </TabsContent>

          <TabsContent value="uses" data-testid="analytics-uses-panel">
            <p className="text-xs text-slate-400 mb-6">Logged-in product usage — what identified Students, Marketing Executives and Admins actually do.</p>
            <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="analytics-identified-events-card">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-2"><UserCircle2 className="h-4 w-4" /> Identified (Logged-in) Events</div>
              <p className="text-3xl font-bold font-mono text-slate-900">{data.uses.identified_events_total}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <HBarCard testId="analytics-role-breakdown-card" title="Usage by Role" data={data.uses.role_breakdown} dataKey="count" labelKey="role" color="#8B5CF6" />
              <HBarCard testId="analytics-feature-breakdown-card" title="Feature Usage Breakdown" data={data.uses.feature_breakdown} dataKey="count" labelKey="event_type" color="#F59E0B" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="analytics-recent-activity-card">
              <h3 className="font-heading text-sm font-semibold text-slate-700 mb-1">Recent Activity by Credential</h3>
              <p className="text-xs text-slate-400 mb-4">Most recent identified actions, mapped to the logged-in user and their location.</p>
              {data.uses.recent_activity.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="analytics-recent-activity-empty">No identified activity yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-4">Credential</th>
                        <th className="py-2 pr-4">Role</th>
                        <th className="py-2 pr-4">Action</th>
                        <th className="py-2 pr-4">Location</th>
                        <th className="py-2">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.uses.recent_activity.map((a, i) => (
                        <tr key={i} className="border-b border-slate-50" data-testid={`recent-activity-row-${i}`}>
                          <td className="py-3 pr-4 font-medium text-slate-700">{a.credential}</td>
                          <td className="py-3 pr-4 text-slate-500">{a.role}</td>
                          <td className="py-3 pr-4 text-slate-500">{a.event_type}</td>
                          <td className="py-3 pr-4 text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{a.city ? `${a.city}, ` : ""}{a.country || "Unknown"}</td>
                          <td className="py-3 text-slate-400 text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}

      <h2 className="font-heading text-xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-2" data-testid="admin-confidence-trends-heading">
        <ShieldAlert className="h-5 w-5 text-indigo-600" /> Assessment Confidence Trends
      </h2>

      {!trends && <p className="text-sm text-slate-400" data-testid="confidence-trends-loading">Loading confidence trends…</p>}

      {trends && (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {["High", "Moderate", "Caution"].map((level) => (
              <div key={level} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`confidence-total-${level.toLowerCase()}`}>
                <div className="flex items-center gap-2 text-xs mb-2" style={{ color: LEVEL_COLORS[level] }}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: LEVEL_COLORS[level] }} /> {level} Confidence
                </div>
                <p className="text-3xl font-bold font-mono text-slate-900">{trends.level_totals[level] || 0}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6 mb-8" data-testid="confidence-trend-chart-card">
            <h3 className="font-heading text-sm font-semibold text-slate-700 mb-4">Confidence Level Over Time (by submission day)</h3>
            {trends.trend.length === 0 ? (
              <p className="text-sm text-slate-400">No completed reports yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={trends.trend} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="High" stackId="v" fill={LEVEL_COLORS.High} />
                  <Bar dataKey="Moderate" stackId="v" fill={LEVEL_COLORS.Moderate} />
                  <Bar dataKey="Caution" stackId="v" fill={LEVEL_COLORS.Caution} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="confidence-flagged-card">
            <h3 className="font-heading text-sm font-semibold text-slate-700 mb-1">Flagged Submissions — Low Validity or Rushed Pacing</h3>
            <p className="text-xs text-slate-400 mb-4">Sorted most-recent first. Review these with the student before relying heavily on their results.</p>
            {trends.flagged.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="confidence-flagged-empty">No flagged submissions right now.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-4">Student</th>
                      <th className="py-2 pr-4">Submitted</th>
                      <th className="py-2 pr-4">Confidence</th>
                      <th className="py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trends.flagged.map((f) => (
                      <tr key={f.report_id} className="border-b border-slate-50" data-testid={`confidence-flagged-row-${f.report_id}`}>
                        <td className="py-3 pr-4 font-medium text-slate-700">{f.student_name}</td>
                        <td className="py-3 pr-4 text-slate-500">{f.generated_at ? new Date(f.generated_at).toLocaleDateString() : "—"}</td>
                        <td className="py-3 pr-4">
                          <span className={cn("text-[11px] font-semibold rounded-full border px-2 py-0.5", LEVEL_BADGE[f.level] || LEVEL_BADGE.Unknown)}>{f.level}</span>
                        </td>
                        <td className="py-3 text-slate-500 max-w-xs">{f.response_pattern_remark || (f.is_rushed ? "Rushed response pace detected." : "Consistency/attention-check flags detected.")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) setConfirmText(""); }}>
        <DialogContent data-testid="analytics-reset-dialog">
          <DialogHeader>
            <DialogTitle>Flush all analytics data?</DialogTitle>
            <DialogDescription>
              This permanently deletes all page-view/behavior/uses event history (traffic, location, feature usage,
              recent activity). Students, reports, licenses and confidence trends (derived from real submitted
              reports) are NOT affected. Type RESET below to confirm.
            </DialogDescription>
          </DialogHeader>
          <Label>Type RESET to confirm</Label>
          <Input value={confirmText} data-testid="analytics-reset-confirm-input" onChange={(e) => setConfirmText(e.target.value)} placeholder="RESET" />
          <DialogFooter>
            <Button
              data-testid="analytics-reset-confirm-button"
              disabled={confirmText !== "RESET" || resetting}
              className="bg-red-600 hover:bg-red-700"
              onClick={runReset}
            >
              {resetting ? "Flushing…" : "Permanently Flush"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
